import * as Y from "yjs";
import { createMutex } from "lib0/mutex";
export const logCollabEvent = async (room, event, details = "") => {
  try {
    const payload = {
      room: room || "unknown",
      event,
      client_id: window.localStorage?.getItem("ove_user_email") || "anonymous",
      details: typeof details === "object" ? JSON.stringify(details) : String(details)
    };
    console.log(`[COLLAB-LOG] [${payload.room}] ${event}:`, details);
    if (typeof fetch !== "undefined") {
      const headers = { "Content-Type": "application/json" };
      const token = window.localStorage?.getItem("ove_token");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      fetch("/api/collab/log", {
        method: "POST",
        headers,
        body: JSON.stringify(payload)
      }).catch(() => {
      });
    }
  } catch (e) {
  }
};
class RelativeSelection {
  constructor(start, end, direction) {
    this.start = start;
    this.end = end;
    this.direction = direction;
  }
}
const createRelativeSelection = (editor, monacoModel, type) => {
  const sel = editor.getSelection();
  if (sel !== null) {
    const startPos = sel.getStartPosition();
    const endPos = sel.getEndPosition();
    const start = Y.createRelativePositionFromTypeIndex(type, monacoModel.getOffsetAt(startPos));
    const end = Y.createRelativePositionFromTypeIndex(type, monacoModel.getOffsetAt(endPos));
    return new RelativeSelection(start, end, sel.getDirection());
  }
  return null;
};
const createMonacoSelectionFromRelativeSelection = (editor, type, relSel, doc, monacoInstance) => {
  const start = Y.createAbsolutePositionFromRelativePosition(relSel.start, doc);
  const end = Y.createAbsolutePositionFromRelativePosition(relSel.end, doc);
  if (start !== null && end !== null && start.type === type && end.type === type) {
    const model = editor.getModel();
    const startPos = model.getPositionAt(start.index);
    const endPos = model.getPositionAt(end.index);
    if (monacoInstance && monacoInstance.Selection) {
      return monacoInstance.Selection.createWithDirection(startPos.lineNumber, startPos.column, endPos.lineNumber, endPos.column, relSel.direction);
    }
  }
  return null;
};
export class MonacoBinding {
  constructor(ytext, monacoModel, editors = /* @__PURE__ */ new Set(), awareness = null, monacoInstance = null, room = "", onRemoteChange = null) {
    this.doc = ytext.doc;
    this.ytext = ytext;
    this.monacoModel = monacoModel;
    this.editors = editors;
    this.monacoInstance = monacoInstance || window.monaco;
    this.room = room;
    this.onRemoteChange = onRemoteChange;
    this.mux = createMutex();
    this._savedSelections = /* @__PURE__ */ new Map();
    logCollabEvent(this.room, "MONACO_BINDING_CONSTRUCTED", {
      initialYTextLen: ytext.toString().length,
      initialModelLen: monacoModel.getValue().length
    });
    this._beforeTransaction = () => {
      this.mux(() => {
        this._savedSelections = /* @__PURE__ */ new Map();
        editors.forEach((editor) => {
          if (editor.getModel() === monacoModel) {
            const rsel = createRelativeSelection(editor, monacoModel, ytext);
            if (rsel !== null) {
              this._savedSelections.set(editor, rsel);
            }
          }
        });
      });
    };
    this.doc.on("beforeAllTransactions", this._beforeTransaction);
    this._editorDisposables = [];
    this._decorations = /* @__PURE__ */ new Map();
    this._rerenderDecorations = () => {
      editors.forEach((editor) => {
        if (awareness && editor.getModel() === monacoModel) {
          const currentDecorations = this._decorations.get(editor) || [];
          const newDecorations = [];
          awareness.getStates().forEach((state, clientID) => {
            if (clientID !== this.doc.clientID && state.selection != null && state.selection.anchor != null && state.selection.head != null) {
              const anchorAbs = Y.createAbsolutePositionFromRelativePosition(state.selection.anchor, this.doc);
              const headAbs = Y.createAbsolutePositionFromRelativePosition(state.selection.head, this.doc);
              if (anchorAbs !== null && headAbs !== null && anchorAbs.type === ytext && headAbs.type === ytext) {
                let start, end, afterContentClassName, beforeContentClassName;
                if (anchorAbs.index < headAbs.index) {
                  start = monacoModel.getPositionAt(anchorAbs.index);
                  end = monacoModel.getPositionAt(headAbs.index);
                  afterContentClassName = "yRemoteSelectionHead yRemoteSelectionHead-" + clientID;
                  beforeContentClassName = null;
                } else {
                  start = monacoModel.getPositionAt(headAbs.index);
                  end = monacoModel.getPositionAt(anchorAbs.index);
                  afterContentClassName = null;
                  beforeContentClassName = "yRemoteSelectionHead yRemoteSelectionHead-" + clientID;
                }
                const range = this.monacoInstance && this.monacoInstance.Range ? new this.monacoInstance.Range(start.lineNumber, start.column, end.lineNumber, end.column) : { startLineNumber: start.lineNumber, startColumn: start.column, endLineNumber: end.lineNumber, endColumn: end.column };
                newDecorations.push({
                  range,
                  options: {
                    className: "yRemoteSelection yRemoteSelection-" + clientID,
                    afterContentClassName,
                    beforeContentClassName
                  }
                });
              }
            }
          });
          this._decorations.set(editor, editor.deltaDecorations(currentDecorations, newDecorations));
        } else {
          this._decorations.delete(editor);
        }
      });
    };
    this._ytextObserver = (event) => {
      if (event.transaction && event.transaction.origin === this) {
        return;
      }
      this.mux(() => {
        let index = 0;
        let opsApplied = 0;
        event.delta.forEach((op) => {
          if (op.retain !== void 0) {
            index += op.retain;
          } else if (op.insert !== void 0) {
            const pos = monacoModel.getPositionAt(index);
            const range = this.monacoInstance && this.monacoInstance.Range ? new this.monacoInstance.Range(pos.lineNumber, pos.column, pos.lineNumber, pos.column) : { startLineNumber: pos.lineNumber, startColumn: pos.column, endLineNumber: pos.lineNumber, endColumn: pos.column };
            const insert = op.insert;
            monacoModel.applyEdits([{ range, text: insert }]);
            opsApplied++;
            index += insert.length;
          } else if (op.delete !== void 0) {
            const pos = monacoModel.getPositionAt(index);
            const endPos = monacoModel.getPositionAt(index + op.delete);
            const range = this.monacoInstance && this.monacoInstance.Range ? new this.monacoInstance.Range(pos.lineNumber, pos.column, endPos.lineNumber, endPos.column) : { startLineNumber: pos.lineNumber, startColumn: pos.column, endLineNumber: endPos.lineNumber, endColumn: endPos.column };
            monacoModel.applyEdits([{ range, text: "" }]);
            opsApplied++;
          }
        });
        this._savedSelections.forEach((rsel, editor) => {
          const sel = createMonacoSelectionFromRelativeSelection(editor, ytext, rsel, this.doc, this.monacoInstance);
          if (sel !== null) {
            editor.setSelection(sel);
          }
        });
        logCollabEvent(this.room, "REMOTE_OPS_APPLIED_TO_MONACO", {
          deltaLength: event.delta.length,
          opsApplied,
          modelLenAfter: monacoModel.getValue().length
        });
        if (opsApplied > 0 && typeof this.onRemoteChange === "function") {
          this.onRemoteChange(monacoModel);
        }
      });
      this._rerenderDecorations();
    };
    ytext.observe(this._ytextObserver);
    const initialYTextValue = ytext.toString();
    const initialModelValue = monacoModel.getValue();
    if (initialYTextValue.length > 0 && initialModelValue !== initialYTextValue) {
      logCollabEvent(this.room, "INITIAL_MONACO_VALUE_SET_FROM_YTEXT", {
        ytextLen: initialYTextValue.length,
        previousModelLen: initialModelValue.length
      });
      this.mux(() => {
        monacoModel.setValue(initialYTextValue);
      });
    } else if (initialYTextValue.length === 0 && initialModelValue.length > 0) {
      logCollabEvent(this.room, "INITIAL_YTEXT_SET_FROM_MONACO", {
        modelLen: initialModelValue.length
      });
      this.mux(() => {
        this.doc.transact(() => {
          if (ytext.length === 0) {
            ytext.insert(0, initialModelValue);
          }
        }, this);
      });
    }
    this._monacoChangeHandler = monacoModel.onDidChangeContent((event) => {
      this.mux(() => {
        logCollabEvent(this.room, "LOCAL_MONACO_CHANGE", {
          numChanges: event.changes.length,
          isUndoing: event.isUndoing,
          isRedoing: event.isRedoing
        });
        this.doc.transact(() => {
          event.changes.sort((change1, change2) => change2.rangeOffset - change1.rangeOffset).forEach((change) => {
            ytext.delete(change.rangeOffset, change.rangeLength);
            ytext.insert(change.rangeOffset, change.text);
          });
        }, this);
      });
    });
    this._monacoDisposeHandler = monacoModel.onWillDispose(() => {
      this.destroy();
    });
    if (awareness) {
      editors.forEach((editor) => {
        const disposable = editor.onDidChangeCursorSelection(() => {
          if (editor.getModel() === monacoModel) {
            const sel = editor.getSelection();
            if (sel === null) {
              return;
            }
            let anchor = monacoModel.getOffsetAt(sel.getStartPosition());
            let head = monacoModel.getOffsetAt(sel.getEndPosition());
            if (this.monacoInstance && sel.getDirection() === this.monacoInstance.SelectionDirection.RTL) {
              const tmp = anchor;
              anchor = head;
              head = tmp;
            }
            awareness.setLocalStateField("selection", {
              anchor: Y.createRelativePositionFromTypeIndex(ytext, anchor),
              head: Y.createRelativePositionFromTypeIndex(ytext, head)
            });
          }
        });
        this._editorDisposables.push(disposable);
        awareness.on("change", this._rerenderDecorations);
      });
      this.awareness = awareness;
    }
  }
  destroy() {
    logCollabEvent(this.room, "MONACO_BINDING_DESTROYED", {
      modelLen: this.monacoModel?.getValue()?.length
    });
    if (this._monacoChangeHandler) this._monacoChangeHandler.dispose();
    if (this._monacoDisposeHandler) this._monacoDisposeHandler.dispose();
    if (this._editorDisposables) {
      this._editorDisposables.forEach((d) => {
        try {
          d.dispose();
        } catch (e) {
        }
      });
      this._editorDisposables = [];
    }
    if (this._decorations) {
      this._decorations.forEach((deco, editor) => {
        try {
          editor.deltaDecorations(deco, []);
        } catch (e) {
        }
      });
      this._decorations.clear();
    }
    this.ytext.unobserve(this._ytextObserver);
    this.doc.off("beforeAllTransactions", this._beforeTransaction);
    if (this.awareness) {
      this.awareness.off("change", this._rerenderDecorations);
    }
  }
}
